import React, { useState, useEffect, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, Legend,
} from "recharts"
import { getArticuloDetalle, getArticuloFacturas, descargarArticuloFacturasExport } from "./api"
import {
  formatCurrency, formatNumber, formatCantidad, formatPercent, formatGrowth, formatFecha,
  claseABC, rotacionConfig, tendenciaConfig, estabilidadConfig, alertaConfig,
  colorPara, DIAS_SEMANA_ORDEN,
} from "./helpers"

const TABS = [
  { id: "resumen",     label: "Resumen",      icon: "mdi-view-dashboard-outline" },
  { id: "tendencia",   label: "Tendencia",    icon: "mdi-chart-line" },
  { id: "facturas",    label: "Facturas",     icon: "mdi-receipt-text-outline" },
  { id: "clientes",    label: "Top clientes", icon: "mdi-account-star-outline" },
  { id: "cocompra",    label: "Co-compra",    icon: "mdi-link-variant" },
  { id: "sucursales",  label: "Sucursales",   icon: "mdi-source-branch" },
  { id: "diasemana",   label: "Día semana",   icon: "mdi-calendar-week" },
]

// ───────── Helpers de UI ─────────

const StatPill = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 180px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "12px 14px",
  }}>
    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 6 }}>
      <i className={`mdi ${icon}`} style={{ color: accent }} />
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
)

StatPill.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const Badge = ({ bg, color, children, title }) => (
  <span title={title} style={{
    display: "inline-block", padding: "2px 10px", borderRadius: 999,
    background: bg, color, fontSize: 11, fontWeight: 700,
  }}>{children}</span>
)

Badge.propTypes = { bg: PropTypes.string, color: PropTypes.string, children: PropTypes.node, title: PropTypes.string }

// Boxplot SVG simple para distribución de precios (min, P25, mediana, P75, P95, max)
const BoxplotPrecios = ({ precios }) => {
  const min = Number(precios?.minimo) || 0
  const p25 = Number(precios?.p25) || 0
  const med = Number(precios?.mediana) || 0
  const p75 = Number(precios?.p75) || 0
  const p95 = Number(precios?.p95) || 0
  const max = Number(precios?.maximo) || 0
  const prom = Number(precios?.promedio) || 0

  const rangoTotal = max - min
  if (!rangoTotal) {
    return (
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
        <h6 style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 700, margin: 0, marginBottom: 6 }}>
          <i className="mdi mdi-currency-usd me-1" />Precio único
        </h6>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{formatCurrency(min)}</div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          Sin variación de precio en el período · Estabilidad: <strong>{precios?.estabilidad || "—"}</strong>
        </div>
      </div>
    )
  }

  // Posiciones en porcentaje del rango
  const pos = v => `${Math.min(100, Math.max(0, ((v - min) / rangoTotal) * 100))}%`

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
      <div className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-2">
        <h6 style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4, margin: 0 }}>
          <i className="mdi mdi-currency-usd me-1" />
          Distribución de precios
        </h6>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>
          CV: <strong style={{ color: "#0f172a" }}>{formatPercent(precios?.coef_variacion_pct ?? 0)}</strong>
          {precios?.estabilidad && <> · Estabilidad: <strong style={{ color: "#0f172a" }}>{precios.estabilidad}</strong></>}
        </span>
      </div>

      {/* Boxplot */}
      <div style={{ position: "relative", height: 60, margin: "12px 8px 4px" }}>
        {/* Whisker line (min ↔ max) */}
        <div style={{
          position: "absolute", top: 28, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, #cbd5e1, #94a3b8)",
        }} />
        {/* Caja P25–P75 */}
        <div style={{
          position: "absolute", top: 12, left: pos(p25),
          width: `calc(${pos(p75)} - ${pos(p25)})`,
          height: 34,
          background: "rgba(59,130,246,0.18)",
          border: "1.5px solid #3b82f6",
          borderRadius: 6,
        }} />
        {/* Línea mediana */}
        <div style={{
          position: "absolute", top: 12, left: pos(med), width: 2, height: 34,
          background: "#1e40af",
        }} />
        {/* Marker P95 */}
        <div style={{
          position: "absolute", top: 18, left: pos(p95),
          width: 12, height: 12, borderRadius: "50%",
          background: "#fff", border: "2px solid #f59e0b",
          transform: "translateX(-6px)",
        }} title={`P95: ${formatCurrency(p95)}`} />
        {/* Marker promedio (rombo) */}
        <div style={{
          position: "absolute", top: 22, left: pos(prom),
          width: 8, height: 8, background: "#10b981",
          transform: "translateX(-4px) rotate(45deg)",
        }} title={`Promedio: ${formatCurrency(prom)}`} />
        {/* Marcadores extremos */}
        <div style={{ position: "absolute", top: 22, left: 0, width: 2, height: 14, background: "#94a3b8" }} />
        <div style={{ position: "absolute", top: 22, right: 0, width: 2, height: 14, background: "#94a3b8" }} />
      </div>

      {/* Etiquetas debajo del boxplot */}
      <div style={{ position: "relative", margin: "0 8px", height: 32 }}>
        <span style={{ position: "absolute", left: 0, fontSize: 10, color: "#94a3b8", transform: "translateX(-50%)" }}>
          {formatCurrency(min)}<div style={{ fontSize: 9 }}>mín</div>
        </span>
        <span style={{ position: "absolute", left: pos(med), fontSize: 10, color: "#1e40af", fontWeight: 700, transform: "translateX(-50%)", textAlign: "center" }}>
          {formatCurrency(med)}<div style={{ fontSize: 9 }}>mediana</div>
        </span>
        <span style={{ position: "absolute", right: 0, fontSize: 10, color: "#94a3b8", transform: "translateX(50%)", textAlign: "right" }}>
          {formatCurrency(max)}<div style={{ fontSize: 9 }}>máx</div>
        </span>
      </div>

      {/* Leyenda */}
      <div style={{ display: "flex", gap: 12, marginTop: 16, fontSize: 10, color: "#64748b", flexWrap: "wrap" }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(59,130,246,0.18)", border: "1.5px solid #3b82f6", marginRight: 4, verticalAlign: "middle" }} />Caja P25–P75 ({formatCurrency(p25)} → {formatCurrency(p75)})</span>
        <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#10b981", transform: "rotate(45deg)", marginRight: 4, verticalAlign: "middle" }} />Promedio {formatCurrency(prom)}</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#fff", border: "2px solid #f59e0b", marginRight: 4, verticalAlign: "middle" }} />P95 {formatCurrency(p95)}</span>
      </div>
    </div>
  )
}

BoxplotPrecios.propTypes = { precios: PropTypes.object }

// ───────── Sub-tabs ─────────

const ResumenSub = ({ producto }) => {
  const c = producto.cantidades || {}
  const f = producto.financiero || {}
  const pr = producto.precios || {}
  const m = producto.movilidad || {}
  const d = producto.distribucion || {}
  const abc = claseABC(producto.abc?.clase)
  const rot = rotacionConfig(m.rotacion)
  const ten = tendenciaConfig(m.tendencia)
  const est = estabilidadConfig(pr.estabilidad)
  const alertas = producto.alertas || []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Badges principales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <Badge bg={abc.bg} color={abc.color} title={abc.desc}>Clase {abc.label}</Badge>
        <Badge bg={rot.bg} color={rot.color}>
          <i className={`mdi mdi-${rot.icon} me-1`} />Rotación {rot.label}
        </Badge>
        <Badge bg="#f1f5f9" color={ten.color}>
          <i className={`mdi mdi-${ten.icon} me-1`} />{ten.label}
        </Badge>
        <Badge bg={est.bg} color={est.color}>Precio {est.label}</Badge>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <StatPill icon="mdi-cash-multiple" label="Importe neto" value={formatCurrency(f.importe_neto)} accent="#10b981"
          sub={`${formatPercent(f.participacion_pct)} del catálogo`} />
        <StatPill icon="mdi-package-variant" label="Cantidad neta" value={formatCantidad(c.neta)} accent="#3b82f6"
          sub={`${formatCantidad(c.vendida)} vendidas - ${formatCantidad(c.devuelta)} devueltas`} />
        <StatPill icon="mdi-receipt-text" label="Facturas" value={formatNumber(d.facturas_count)} accent="#6366f1" />
        <StatPill icon="mdi-account-group" label="Clientes únicos" value={formatNumber(d.clientes_unicos)} accent="#8b5cf6" />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <StatPill icon="mdi-currency-usd" label="Precio promedio" value={formatCurrency(pr.promedio)} accent="#f59e0b"
          sub={`CV ${formatPercent(pr.coef_variacion_pct)}`} />
        <StatPill icon="mdi-cash" label="Ticket promedio" value={formatCurrency(f.ticket_promedio_factura)} accent="#10b981" />
        <StatPill icon="mdi-source-branch" label="Sucursales" value={`${formatNumber(d.sucursales_vendido)}/${formatNumber(d.sucursales_total_periodo)}`}
          accent="#06b6d4" sub={`${formatPercent(d.penetracion_sucursales_pct)} de penetración`} />
        <StatPill icon="mdi-calendar-check" label="Días con ventas"
          value={`${formatNumber(m.dias_con_ventas)}/${formatNumber(m.dias_periodo)}`}
          accent="#84cc16" sub={`${formatPercent(m.penetracion_temporal_pct)} de penetración`} />
      </div>

      {/* Rango de precios — boxplot */}
      <BoxplotPrecios precios={pr} />


      {/* Período */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: "#64748b" }}>
        <span><strong>Primera venta:</strong> {formatFecha(d.primera_venta)}</span>
        <span><strong>Última venta:</strong> {formatFecha(d.ultima_venta)}</span>
        {Array.isArray(d.tipos_comprobante) && (
          <span><strong>Tipos doc:</strong> {d.tipos_comprobante.join(", ")}</span>
        )}
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <div>
          <h6 style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
            <i className="mdi mdi-alert me-1" />
            {alertas.length} alerta{alertas.length !== 1 ? "s" : ""}
          </h6>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {alertas.map((a, i) => {
              const cfg = alertaConfig(a.tipo || a)
              return (
                <div key={i} style={{ background: cfg.bg, color: cfg.color, padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
                  <i className={`mdi mdi-${cfg.icon} me-1`} />
                  <strong>{cfg.label}</strong>
                  {a.mensaje && <span style={{ marginLeft: 6 }}>· {a.mensaje}</span>}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

ResumenSub.propTypes = { producto: PropTypes.object }

const TendenciaSub = ({ detalle }) => {
  const serie = detalle?.serie_diaria || []
  const comparativo = detalle?.comparativo || {}

  if (serie.length === 0) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin datos de serie diaria.</div>
  }

  const crecAnt = formatGrowth(comparativo.crecimiento_importe_pct)
  const crecYoY = formatGrowth(comparativo.crecimiento_importe_yoy_pct)

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Comparativos */}
      {(comparativo.crecimiento_importe_pct != null || comparativo.crecimiento_importe_yoy_pct != null) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {comparativo.crecimiento_importe_pct != null && (
            <div style={{
              flex: "1 1 280px", background: "#fff", border: "1px solid #e2e8f0",
              borderLeft: `4px solid ${crecAnt.color}`, borderRadius: 10, padding: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                vs período anterior
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: crecAnt.color, marginTop: 4 }}>
                <i className={`mdi mdi-${crecAnt.icon} me-1`} />{crecAnt.text}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                {formatCurrency(comparativo.periodo_anterior_importe)} → {formatCurrency(comparativo.periodo_actual_importe)}
              </div>
            </div>
          )}
          {comparativo.crecimiento_importe_yoy_pct != null && (
            <div style={{
              flex: "1 1 280px", background: "#fff", border: "1px solid #e2e8f0",
              borderLeft: `4px solid ${crecYoY.color}`, borderRadius: 10, padding: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                vs año anterior
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: crecYoY.color, marginTop: 4 }}>
                <i className={`mdi mdi-${crecYoY.icon} me-1`} />{crecYoY.text}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gráfico serie diaria */}
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 16 }}>
        <h6 style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
          <i className="mdi mdi-chart-line me-2 text-primary" />
          Importe diario
        </h6>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={serie} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={f => f?.substring(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.[0]) return null
                  const d = payload[0].payload
                  return (
                    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 700 }}>{label}</div>
                      <div>{formatCurrency(d.importe)}</div>
                      <div style={{ color: "#94a3b8", fontSize: 11 }}>
                        {formatCantidad(d.cantidad)} unidades · {formatNumber(d.facturas)} facturas
                      </div>
                    </div>
                  )
                }}
              />
              <Line type="monotone" dataKey="importe" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

TendenciaSub.propTypes = { detalle: PropTypes.object }

const ClientesSub = ({ detalle }) => {
  const clientes = detalle?.top_clientes || []
  if (clientes.length === 0) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin datos de clientes top.</div>
  }
  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f1f5f9" }}>
            <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Cliente</th>
            <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Facturas</th>
            <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Cantidad</th>
            <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Importe</th>
            <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Última compra</th>
          </tr>
        </thead>
        <tbody>
          {clientes.map((c, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "8px 12px", color: "#0f172a", fontWeight: 500 }}>
                {c.nombre || "—"}
                <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                  NIT: {c.nit}
                </div>
              </td>
              <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>{formatNumber(c.facturas)}</td>
              <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>{formatCantidad(c.cantidad)}</td>
              <td style={{ padding: "8px 12px", textAlign: "right", color: "#0f172a", fontWeight: 700 }}>{formatCurrency(c.importe)}</td>
              <td style={{ padding: "8px 12px", textAlign: "right", color: "#94a3b8", fontSize: 12 }}>{formatFecha(c.ultima_compra)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

ClientesSub.propTypes = { detalle: PropTypes.object }

const CoCompraSub = ({ detalle, productoActual, onCompararConCoPurchases }) => {
  const items = detalle?.co_purchase || []
  if (items.length === 0) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin patrones de co-compra detectados.</div>
  }
  const top3 = items.slice(0, 3)
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{
        background: "#eff6ff", border: "1px solid #bfdbfe",
        color: "#1e40af", padding: "10px 14px", borderRadius: 8, fontSize: 13,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <i className="mdi mdi-lightbulb-on-outline me-1" />
          <strong>Insight:</strong> productos que se venden frecuentemente con éste. Un <strong>lift {">"}1</strong> indica
          afinidad real (no por azar). Útil para combos y promociones cruzadas.
        </div>
        {productoActual && top3.length >= 1 && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => onCompararConCoPurchases?.([productoActual, ...top3])}
            title="Abre el comparador con este producto y sus 3 mejores co-compras precargados"
          >
            <i className="mdi mdi-compare-horizontal me-1" />
            Comparar con top {top3.length}
          </button>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Producto relacionado</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Co-facturas</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Lift</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Confianza</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => {
              const lift = Number(p.lift) || 0
              const liftColor = lift >= 3 ? "#10b981" : lift >= 1.5 ? "#f59e0b" : "#475569"
              return (
                <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "8px 12px", color: "#0f172a", fontWeight: 500 }}>
                    {p.descripcion || "—"}
                    <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                      ID: {p.id_producto}
                    </div>
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>{formatNumber(p.co_facturas)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: liftColor, fontWeight: 700 }}>
                    {Number(p.lift).toFixed(2)}×
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>{formatPercent(p.confianza)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

CoCompraSub.propTypes = {
  detalle: PropTypes.object,
  productoActual: PropTypes.object,
  onCompararConCoPurchases: PropTypes.func,
}

const SucursalesSub = ({ detalle }) => {
  const items = detalle?.por_sucursal || []
  if (items.length === 0) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin distribución por sucursal.</div>
  }
  const datos = items.map((s, i) => ({ ...s, color: colorPara(i), nombreCorto: (s.sucursal || "—").length > 20 ? `${s.sucursal.slice(0, 19)}…` : s.sucursal }))

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 16 }}>
        <h6 style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
          <i className="mdi mdi-source-branch me-2 text-primary" />
          Importe por sucursal
        </h6>
        <div style={{ width: "100%", height: Math.max(220, datos.length * 28) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datos} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
              <YAxis type="category" dataKey="nombreCorto" width={170} tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null
                  const d = payload[0].payload
                  return (
                    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 700 }}>{d.sucursal}</div>
                      <div>{formatCurrency(d.importe)}</div>
                      <div style={{ color: "#94a3b8", fontSize: 11 }}>
                        {formatCantidad(d.cantidad)} unidades · {formatPercent(d.participacion_pct)}
                      </div>
                    </div>
                  )
                }}
              />
              <Bar dataKey="importe" radius={[0, 4, 4, 0]}>
                {datos.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

SucursalesSub.propTypes = { detalle: PropTypes.object }

const DiaSemanaSub = ({ detalle }) => {
  // useMemo deben ir SIEMPRE antes de cualquier early-return (reglas de hooks)
  const raw = useMemo(() => detalle?.por_dia_semana || [], [detalle])

  const ordenados = useMemo(() => {
    return DIAS_SEMANA_ORDEN.map(d => raw.find(r => r.dia_semana === d) || { dia_semana: d, cantidad: 0, importe: 0 })
  }, [raw])

  const mejor = useMemo(() => raw.reduce((m, r) => r.importe > (m?.importe || 0) ? r : m, null), [raw])
  const peor = useMemo(() => {
    const conVentas = raw.filter(r => r.importe > 0)
    if (conVentas.length === 0) return null
    return conVentas.reduce((m, r) => r.importe < (m?.importe || Infinity) ? r : m, null)
  }, [raw])

  if (raw.length === 0) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin patrón por día de semana.</div>
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 16 }}>
        <h6 style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
          <i className="mdi mdi-calendar-week me-2 text-primary" />
          Patrón por día de la semana
        </h6>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ordenados}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="dia_semana" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.[0]) return null
                  const d = payload[0].payload
                  return (
                    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 700 }}>{label}</div>
                      <div>{formatCurrency(d.importe)}</div>
                      <div style={{ color: "#94a3b8", fontSize: 11 }}>
                        {formatCantidad(d.cantidad)} unidades
                      </div>
                    </div>
                  )
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="importe" name="Importe" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: "flex", justifyContent: "space-around", marginTop: 12, fontSize: 13 }}>
          <span style={{ color: "#10b981" }}>
            <i className="mdi mdi-trophy me-1" />Mejor: <strong>{mejor?.dia_semana || "—"}</strong>
          </span>
          {peor && (
            <span style={{ color: "#ef4444" }}>
              <i className="mdi mdi-trending-down me-1" />Más bajo: <strong>{peor?.dia_semana || "—"}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

DiaSemanaSub.propTypes = { detalle: PropTypes.object }

// Pequeño helper inline para copiar CUFE al clipboard
const CopiarCufeMini = ({ cufe }) => {
  const [copiado, setCopiado] = useState(false)
  if (!cufe) return null
  const copiar = async (e) => {
    e.stopPropagation()
    try { await navigator.clipboard.writeText(cufe); setCopiado(true); setTimeout(() => setCopiado(false), 1500) }
    catch { /* noop */ }
  }
  return (
    <button type="button" onClick={copiar}
      title={copiado ? "Copiado" : "Copiar CUFE completo"}
      style={{
        background: "none", border: "none", padding: 2, cursor: "pointer",
        color: copiado ? "#10b981" : "#94a3b8",
        display: "inline-flex", alignItems: "center",
      }}>
      <i className={`mdi mdi-${copiado ? "check" : "content-copy"}`} style={{ fontSize: 13 }} />
    </button>
  )
}

CopiarCufeMini.propTypes = { cufe: PropTypes.string }

const PAGE_SIZE_FACTURAS = 100

const FacturasSub = ({ idProducto, filtros }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [page, setPage] = useState(0)
  const [exportando, setExportando] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)

  const cargar = useCallback(async (pageIdx) => {
    if (!idProducto) return
    setLoading(true)
    setError("")
    try {
      const { data: response } = await getArticuloFacturas(idProducto, {
        ...filtros,
        limit: PAGE_SIZE_FACTURAS,
        offset: pageIdx * PAGE_SIZE_FACTURAS,
      })
      setData(response)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el listado de facturas.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [idProducto, filtros])

  useEffect(() => {
    setPage(0)
    cargar(0)
  }, [cargar])

  const handleExportar = async (formato) => {
    if (!idProducto || exportando) return
    setExportando(formato)
    setExportInfo(null)
    setError("")
    try {
      const info = await descargarArticuloFacturasExport(idProducto, { ...filtros, formato })
      setExportInfo({ formato, totalRows: info.totalRows, filename: info.filename })
      setTimeout(() => setExportInfo(null), 6000)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const facturas = data?.facturas || []
  const meta = data?.metadata || data?.paginacion || {}
  const total = Number(meta.total_facturas) || facturas.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE_FACTURAS))

  if (loading && !data) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <Spinner color="primary" /> <div className="mt-2">Cargando facturas…</div>
      </div>
    )
  }
  if (error) {
    return (
      <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 8 }}>
        <i className="mdi mdi-alert-circle me-1" />{error}
      </div>
    )
  }
  if (!data) return null

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Stats agregadas */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <StatPill icon="mdi-receipt-text" label="Facturas totales" value={formatNumber(meta.total_facturas)} accent="#3b82f6" />
        <StatPill icon="mdi-cash-multiple" label="Importe neto" value={formatCurrency(meta.total_importe_neto)} accent="#10b981"
          sub={meta.total_impuesto != null ? `${formatCurrency(meta.total_impuesto)} impuestos` : null} />
        <StatPill icon="mdi-package-variant" label="Cantidad total" value={formatCantidad(meta.total_cantidad)} accent="#8b5cf6" />
        <StatPill icon="mdi-cash" label="Ticket promedio" value={formatCurrency(meta.ticket_promedio)} accent="#f59e0b"
          sub={meta.precio_promedio != null ? `Precio prom: ${formatCurrency(meta.precio_promedio)}` : null} />
        <StatPill icon="mdi-account-group" label="Clientes únicos" value={formatNumber(meta.clientes_unicos)} accent="#06b6d4" />
        <StatPill icon="mdi-source-branch" label="Sucursales" value={formatNumber(meta.sucursales_unicas)} accent="#84cc16" />
      </div>

      {/* Header con exports */}
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 16 }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
              <i className="mdi mdi-receipt-text-outline me-2 text-primary" />
              Facturas que contienen este producto
            </h6>
            {(meta.primera_factura || meta.ultima_factura) && (
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                {formatFecha(meta.primera_factura)} → {formatFecha(meta.ultima_factura)}
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 12, color: "#64748b" }}>Pág. {page + 1} / {totalPages}</span>
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className="btn btn-outline-success"
                onClick={() => handleExportar("xlsx")} disabled={!!exportando}>
                {exportando === "xlsx" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-excel me-1" /> Excel</>)}
              </button>
              <button type="button" className="btn btn-outline-primary"
                onClick={() => handleExportar("csv")} disabled={!!exportando}>
                {exportando === "csv" ? (<><Spinner size="sm" className="me-1" /> Generando…</>) : (<><i className="mdi mdi-file-delimited me-1" /> CSV</>)}
              </button>
            </div>
          </div>
        </div>

        {exportInfo && (
          <div style={{
            background: "#d1fae5", border: "1px solid #a7f3d0",
            color: "#065f46", padding: "8px 12px", borderRadius: 8,
            fontSize: 12, marginBottom: 10, display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="mdi mdi-check-circle" />
            <span>
              Descarga lista{exportInfo.totalRows ? `: ${formatNumber(exportInfo.totalRows)} filas` : ""} · {exportInfo.filename}
            </span>
            <button type="button" className="btn-close ms-auto" style={{ fontSize: 9 }} onClick={() => setExportInfo(null)} />
          </div>
        )}

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9", zIndex: 1 }}>
                <tr>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Folio</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Fecha</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Sucursal</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Receptor</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Cant.</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Importe prod.</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Total fact.</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>%</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>CUFE</th>
                </tr>
              </thead>
              <tbody>
                {facturas.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#64748b" }}>
                    Sin facturas en el período.
                  </td></tr>
                )}
                {facturas.map((f, i) => {
                  const recortarRec = (f.nombre_receptor || "—")
                  const recCorto = recortarRec.length > 30 ? `${recortarRec.slice(0, 29)}…` : recortarRec
                  return (
                    <tr key={f.cufe || i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 10px", color: "#0f172a", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, fontWeight: 600 }}>
                        {f.prefijo}-{f.folio}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#475569", fontSize: 12 }}>{formatFecha(f.fecha)}</td>
                      <td style={{ padding: "8px 10px", color: "#475569", fontSize: 12 }}>
                        <i className="mdi mdi-store text-primary me-1" />{f.sucursal || "—"}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#475569", fontSize: 12 }} title={recortarRec}>
                        {recCorto}
                        <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                          {f.nit_receptor}
                        </div>
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: "#475569" }}>{formatCantidad(f.cantidad_producto)}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: "#10b981", fontWeight: 600 }}>{formatCurrency(f.importe_producto)}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: "#0f172a", fontWeight: 700 }}>{formatCurrency(f.total_factura)}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: "#475569", fontSize: 12 }}>{formatPercent(f.participacion_pct)}</td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}><CopiarCufeMini cufe={f.cufe} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { const p = Math.max(0, page - 1); setPage(p); cargar(p) }}
              disabled={page === 0 || loading}>
              <i className="mdi mdi-chevron-left" /> Anterior
            </button>
            <span style={{ fontSize: 12, color: "#64748b" }}>
              {loading && <Spinner size="sm" className="me-2" />}
              Mostrando {page * PAGE_SIZE_FACTURAS + 1}–{Math.min((page + 1) * PAGE_SIZE_FACTURAS, total)} de {formatNumber(total)}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { const p = Math.min(totalPages - 1, page + 1); setPage(p); cargar(p) }}
              disabled={page + 1 >= totalPages || loading}>
              Siguiente <i className="mdi mdi-chevron-right" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

FacturasSub.propTypes = { idProducto: PropTypes.string, filtros: PropTypes.object }

// ───────── Drawer principal ─────────

const DrawerDetalleProducto = ({ abierto, idProducto, productoBase, filtros, onCerrar, onCompararConCoPurchases }) => {
  const [detalle, setDetalle] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [tabActivo, setTabActivo] = useState("resumen")

  useEffect(() => {
    if (!abierto || !idProducto) return
    let cancelled = false
    setLoading(true)
    setError("")
    setDetalle(null)
    setTabActivo("resumen")
    getArticuloDetalle(idProducto, filtros)
      .then(res => {
        if (cancelled) return
        const payload = res.data
        if (payload?.success === false) {
          throw new Error(payload?.message || payload?.error || "Error al cargar detalle.")
        }
        setDetalle(payload?.data || payload)
      })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.error || err.message || "No se pudo cargar el detalle.")
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [abierto, idProducto, filtros])

  useEffect(() => {
    if (!abierto) return
    const h = e => { if (e.key === "Escape") onCerrar() }
    window.addEventListener("keydown", h)
    return () => window.removeEventListener("keydown", h)
  }, [abierto, onCerrar])

  // Aplanar el shape: el detalle del back trae dimensiones dentro de `resumen`,
  // mientras que la lista las trae directo. Mezclamos para que ResumenSub funcione igual.
  const producto = useMemo(() => {
    const base = productoBase || {}
    const det = detalle || {}
    const r = det.resumen || {}
    return { ...base, ...det, ...r }
  }, [productoBase, detalle])

  if (!abierto) return null

  const desc = producto.descripcion || productoBase?.descripcion || "—"

  return (
    <>
      <div onClick={onCerrar} style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)",
        zIndex: 1040, animation: "fadeIn 0.2s ease",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0,
        height: "100vh", width: "min(1100px, 95vw)",
        background: "#f8fafc", zIndex: 1050,
        boxShadow: "-8px 0 32px rgba(15, 23, 42, 0.15)",
        display: "flex", flexDirection: "column",
        animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>
        <style>{`
          @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>

        {/* Header */}
        <div style={{
          padding: "16px 24px",
          background: "linear-gradient(135deg, #0f172a, #1e3a5f)",
          color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Detalle de producto
            </div>
            <h4 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#fff" }}>
              {desc}
            </h4>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 4, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
              ID: {idProducto}
            </div>
          </div>
          <button type="button" onClick={onCerrar} style={{
            background: "rgba(255,255,255,0.1)", border: "none", color: "#fff",
            width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 20,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }} title="Cerrar (Esc)">
            <i className="mdi mdi-close" />
          </button>
        </div>

        {/* Tabs */}
        {!loading && !error && (
          <div style={{
            background: "#fff", borderBottom: "1px solid #e2e8f0",
            padding: "0 24px",
            display: "flex", gap: 4, overflowX: "auto",
          }}>
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              return (
                <button key={tab.id} type="button" onClick={() => setTabActivo(tab.id)} style={{
                  background: "none", border: "none", padding: "12px 16px",
                  fontSize: 13, fontWeight: 600, color: activo ? "#0f172a" : "#64748b",
                  borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                }}>
                  <i className={`mdi ${tab.icon}`} />
                  {tab.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {loading && (
            <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
              <Spinner color="primary" /> <div className="mt-2">Cargando detalle…</div>
            </div>
          )}
          {error && (
            <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10 }}>
              <i className="mdi mdi-alert-circle me-2" /> {error}
            </div>
          )}
          {!loading && !error && (
            <>
              {tabActivo === "resumen"   && <ResumenSub producto={producto} />}
              {tabActivo === "tendencia" && <TendenciaSub detalle={producto} />}
              {tabActivo === "facturas"  && <FacturasSub idProducto={idProducto} filtros={filtros} />}
              {tabActivo === "clientes"  && <ClientesSub detalle={producto} />}
              {tabActivo === "cocompra"  && (
                <CoCompraSub
                  detalle={producto}
                  productoActual={producto}
                  onCompararConCoPurchases={onCompararConCoPurchases}
                />
              )}
              {tabActivo === "sucursales" && <SucursalesSub detalle={producto} />}
              {tabActivo === "diasemana" && <DiaSemanaSub detalle={producto} />}
            </>
          )}
        </div>
      </div>
    </>
  )
}

DrawerDetalleProducto.propTypes = {
  abierto: PropTypes.bool.isRequired,
  idProducto: PropTypes.string,
  productoBase: PropTypes.object,
  filtros: PropTypes.object,
  onCerrar: PropTypes.func.isRequired,
  onCompararConCoPurchases: PropTypes.func,
}

export default DrawerDetalleProducto
